// The dashboard's cards on a narrow screen: one under another in the owner's order, some of them
// hidden (the owner: "daj mi możliwość zmiany kolejności tych okienek"). A wide screen places them
// on a board instead (dashboard-grid.ts). Kept on this device: a phone and a desktop lay out differently.

export const WIDGETS = ['overview', 'metrics', 'flow', 'upcoming', 'settlement', 'changes', 'recent', 'savings', 'heatmap'] as const;
export type WidgetId = (typeof WIDGETS)[number];

/**
 * Cards that repeat what the overview already says stay hidden until the owner shows them:
 * spending by category (the flow draws it), the latest entries (Transactions), savings (the
 * snapshot and the accounts) and the heatmap.
 */
export const HIDDEN_BY_DEFAULT: readonly WidgetId[] = ['changes', 'recent', 'savings', 'heatmap'];

export type DashboardLayout = { order: WidgetId[]; hidden: WidgetId[] };

export const DEFAULT_LAYOUT: DashboardLayout = { order: [...WIDGETS], hidden: [...HIDDEN_BY_DEFAULT] };

/**
 * A phone reads one card at a time, so what matters most comes first (the owner's brief): money
 * available and safe to spend, the month's figures, what is due (the budget is in the flow).
 */
export const PHONE_ORDER: readonly WidgetId[] = ['overview', 'metrics', 'upcoming', 'settlement', 'flow', 'changes', 'recent', 'savings', 'heatmap'];

/** The default for this screen: the phone order on a narrow one. */
export function defaultLayout(narrow: boolean): DashboardLayout {
  return narrow ? { order: [...PHONE_ORDER], hidden: [...HIDDEN_BY_DEFAULT] } : DEFAULT_LAYOUT;
}

const known = (id: unknown): id is WidgetId => typeof id === 'string' && (WIDGETS as readonly string[]).includes(id);

/**
 * A stored layout, made whole: cards it does not know are dropped, and a card added since goes
 * where it stands by default — after the card that precedes it there.
 */
export function parseLayout(raw: string | null, narrow = false): DashboardLayout {
  let stored: { order?: unknown; hidden?: unknown } = {};
  try {
    stored = raw ? (JSON.parse(raw) as typeof stored) : {};
  } catch {
    stored = {};
  }
  const order = [...new Set(Array.isArray(stored.order) ? stored.order.filter(known) : [])];
  const reference = narrow ? PHONE_ORDER : WIDGETS;
  for (const [index, id] of reference.entries()) {
    if (order.includes(id)) continue;
    const before = reference.slice(0, index).reverse().find((w) => order.includes(w));
    order.splice(before ? order.indexOf(before) + 1 : 0, 0, id);
  }
  // Nothing usable stored: the cards hidden by default stay hidden.
  const hidden = [...new Set(Array.isArray(stored.hidden) ? stored.hidden.filter(known) : HIDDEN_BY_DEFAULT)];
  return { order, hidden };
}

/** The layout with `id` moved to `to` (an index in the order). */
export function moveWidget(layout: DashboardLayout, id: WidgetId, to: number): DashboardLayout {
  const order = layout.order.filter((w) => w !== id);
  order.splice(Math.max(0, Math.min(order.length, to)), 0, id);
  return { ...layout, order };
}

export function setHidden(layout: DashboardLayout, id: WidgetId, hidden: boolean): DashboardLayout {
  return { ...layout, hidden: hidden ? [...new Set([...layout.hidden, id])] : layout.hidden.filter((w) => w !== id) };
}

/** What to store: nothing for the default layout, so a later default applies again. */
export function storedLayout(layout: DashboardLayout, narrow = false): string | null {
  const reference = narrow ? PHONE_ORDER : WIDGETS;
  const same =
    layout.hidden.length === HIDDEN_BY_DEFAULT.length &&
    HIDDEN_BY_DEFAULT.every((id) => layout.hidden.includes(id)) &&
    layout.order.every((id, i) => id === reference[i]);
  return same ? null : JSON.stringify(layout);
}
